import { Link } from '@tanstack/react-router';
import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import { Check, Clock, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type Batch,
  useGetParentsOnboardingBatches,
} from '~entities/parent/api/get-parents-onboarding-batches';
import { useParentsOnboardingBatchesEmailReleaseMutation } from '~entities/parent/api/parents-onboarding-batches-email-release';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  EnrollioTable,
  EnrollioTableSelectFilter,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';

// Create a column helper
const columnHelper = createColumnHelper<Batch>();

export const ParentsOnboardingBatchesTable = () => {
  const { formatInStudioTz } = useStudioDateFormatter();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: batches, isFetching } = useGetParentsOnboardingBatches();

  const { mutateAsync: processBatch, isPending } =
    useParentsOnboardingBatchesEmailReleaseMutation();

  const handleProcessBatch = useCallback(
    async (batchId: string) => {
      try {
        await processBatch({ batchId });
        toast.success('Batch processed successfully');
      } catch (error) {
        toast.error('Failed to process batch');
        console.error(error);
      }
    },
    [processBatch],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Created Date'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            <Link
              to={'/parents/csv-batch-processing/$id'}
              params={{
                id: info.row.original.id,
              }}
            >
              {formatInStudioTz(info.getValue(), 'MMM dd, yyyy h:mm a')}
            </Link>
          </div>
        ),
      }),
      columnHelper.accessor('count', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Total Records'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4 font-bold'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('successCount', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Successful'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='flex items-center gap-2 px-4'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('failedCount', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Failed'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='flex items-center gap-2 px-4'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('isReleased', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Status'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const isReleased = info.getValue();
          const finishedAt = info.row.original.finishedAt;

          if (isReleased && finishedAt) {
            return (
              <div className='px-4'>
                <Badge
                  variant='outline'
                  className='bg-green-100 text-green-800'
                >
                  <Check className='mr-1 size-3' />
                  Completed
                </Badge>
              </div>
            );
          } else if (isReleased && !finishedAt) {
            return (
              <div className='px-4'>
                <Badge variant='outline' className='bg-blue-100 text-blue-800'>
                  <Clock className='mr-1 size-3' />
                  Processing
                </Badge>
              </div>
            );
          } else {
            return (
              <div className='px-4'>
                <Badge
                  variant='outline'
                  className='bg-amber-100 text-amber-800'
                >
                  <Clock className='mr-1 size-3' />
                  Pending
                </Badge>
              </div>
            );
          }
        },
      }),
      columnHelper.accessor('finishedAt', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Completed At'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const finishedAt = info.getValue();
          return (
            <div className='px-4'>
              {finishedAt
                ? formatInStudioTz(finishedAt, 'MMM dd, yyyy h:mm a')
                : '-'}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Actions'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const batch = info.row.original;
          const isReleased = batch.isReleased;

          if (!isReleased) {
            return (
              <div className='px-4'>
                <Button
                  variant='outline'
                  size='sm'
                  className='flex items-center gap-2'
                  onClick={() => {
                    void handleProcessBatch(batch.id);
                  }}
                  disabled={isPending}
                >
                  Onboard Parents
                  {isPending && <Loader2 className='size-4 animate-spin' />}
                </Button>
              </div>
            );
          }

          return <div className='px-4'>-</div>;
        },
      }),
    ],
    [formatInStudioTz, handleProcessBatch, isPending],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Parent Onboarding Email Batches</CardTitle>
      </CardHeader>
      <CardContent>
        {isFetching ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='size-8 animate-spin text-primary' />
          </div>
        ) : (
          <EnrollioTable
            ariaLabel='Parent onboarding email batches'
            totalLabel='Email batches'
            filterHeader={(table) => {
              const status = table.getColumn('isReleased')?.getFilterValue();
              return (
                <EnrollioTableSelectFilter
                  value={
                    status === true
                      ? 'released'
                      : status === false
                        ? 'pending'
                        : 'all'
                  }
                  onChange={(value) => {
                    table
                      .getColumn('isReleased')
                      ?.setFilterValue(
                        value === 'all' ? undefined : value === 'released',
                      );
                  }}
                  placeholder='Filter by status'
                  options={[
                    { label: 'All statuses', value: 'all' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Released', value: 'released' },
                  ]}
                />
              );
            }}
            columns={columns}
            data={batches ?? []}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        )}
      </CardContent>
    </Card>
  );
};
